/**
 * DOM contract for animating a Popover's backdrop and for its click mask.
 *
 * A native popover's backdrop is the `::backdrop` pseudo-element, which GSAP
 * cannot animate and which takes no pointer events (`pointer-events: none
 * !important` in the UA stylesheet). So IX3 animates it through CSS: the
 * runtime turns each Backdrop action into a `@keyframes` rule and scrubs it from
 * custom properties on the popover, which `::backdrop` inherits.
 * A Backdrop trigger gets a real element to click instead — an invisible mask
 * shown in the top layer directly below its popover.
 *
 * These live here rather than in `plugins-utils/Popover` because
 * dependency-sensitive places need them: the atom and the published IX3
 * runtime (which holds a size budget enforced by
 * `plugins/IX3/bundle-size-manifest.json`). `core-constants` has no runtime
 * dependencies of its own, so importing it costs nothing anywhere.
 */

/**
 * Class on the popover element itself.
 *
 * The IX3 runtime uses it to recognise a popover from a trigger element, which is
 * how `wf:popover-element` resolves the popover that actually opened rather than
 * a stored path.
 */
export const POPOVER_CLASS = 'w-popover';

/**
 * Custom property on the popover holding its backdrop's `animation` list, one
 * entry per IX3 Backdrop tween:
 * `w-bd-<n> 1s linear calc(var(--w-bd-<n>, 1) * -1s) both paused`.
 *
 * Bootflow applies it to `::backdrop`, which inherits custom properties from
 * its popover. Each entry is a paused animation whose delay is set by that
 * action's own progress variable, so GSAP scrubs it by tweening one number. An
 * unset list is `none`: the backdrop shows its authored styles.
 */
export const POPOVER_BACKDROP_ANIMATION_PROPERTY = '--w-backdrop-animation';

/**
 * Marker the runtime writes onto a popover beside
 * `POPOVER_BACKDROP_ANIMATION_PROPERTY`, and removes with it.
 *
 * Bootflow's `::backdrop` rule keys on it, so a popover without a Backdrop
 * animation matches nothing.
 */
export const POPOVER_BACKDROP_ANIMATED_ATTRIBUTE =
  'data-w-popover-backdrop-animated';

/**
 * Prefix of a Backdrop tween's `@keyframes` name and of its progress custom
 * property (`w-bd-<n>`, `--w-bd-<n>`), numbered by the runtime as it builds
 * them.
 *
 * The keyframes leave out the end the action does not set, so the browser fills
 * it from the authored `::backdrop` styles: a To tween starts from them and a
 * From tween ends on them. Transforms are written as the individual
 * `translate`, `scale` and `rotate` properties, with Skew as `transform:
 * skew()`; with a Perspective, a rotation and a Z-axis Move join that
 * `transform` after `perspective()` instead. Two actions that write the same
 * property do not combine; the later one wins.
 */
export const POPOVER_BACKDROP_KEYFRAMES_PREFIX = 'w-bd-';

/** Class on the click mask. */
export const POPOVER_MASK_CLASS = 'w-popover-mask';

/**
 * Points a click mask at the DOM id of the popover it shields, so IX3's
 * `wf:popover-backdrop` trigger resolves the right popover when it is inside a
 * component or a repeating container.
 *
 * The mask is a `popover="manual"` element the IX3 runtime inserts directly
 * after a popover whose interaction has a trigger on Backdrop.
 * `webflow-popover.js` shows it in the top layer directly below the popover, so
 * it takes the clicks a native `::backdrop` never receives.
 */
export const POPOVER_MASK_FOR_ATTRIBUTE = 'data-w-popover-mask-for';

/**
 * Marker the `wf:popover` trigger writes onto a popover element while it is
 * bound, saying this popover has an open/close animation.
 *
 * `webflow-popover.js` keys on it, and only acts for a popover that carries
 * it, which is why it lives here beside the rest of the DOM contract rather
 * than in the IX3 package that writes it: the module and the writer would
 * otherwise share nothing but a hand-copied string.
 */
export const POPOVER_ANIMATED_ATTRIBUTE = 'data-w-popover-animated';

/**
 * Carries the popover's AUTHORED mode, so `webflow-popover.js` knows what mode
 * to hand back once a close it held up is done.
 *
 * It cannot read the `popover` attribute for this: an animated popover is
 * switched to `manual` at runtime so its close timeline can finish before the
 * element leaves the top layer, so the attribute stops reporting what the author
 * chose exactly when the answer is needed.
 */
export const POPOVER_MODE_ATTRIBUTE = 'data-w-popover-mode';

/**
 * DOM event carrying a command *to* a popover. The Designer already listens for
 * it on the canvas (`listenForPopoverCommands`); `webflow-popover.js` listens
 * for it on published pages and in preview.
 *
 * Declared here so the published site module — plain JS that cannot import —
 * has a canonical value to be checked against by its drift test.
 */
export const POPOVER_COMMAND_EVENT = 'webflow:popover-command';

/**
 * Commit the popover open, or closed, WITHOUT animating.
 *
 * IX3 sends these when a trigger other than the popover's own state change
 * drives its Open or Close timeline — a Click, Exit intent or Duration assigned
 * to the role. The timeline is already playing, so the receiver only does the
 * DOM half: `showPopover()` / `hidePopover()` plus stack bookkeeping. It must
 * not re-announce the state change, or the same timeline plays twice.
 */
export const POPOVER_ANIMATION_OPEN_COMMAND = 'animation-open';
export const POPOVER_ANIMATION_CLOSE_COMMAND = 'animation-close';
